'use client'

import Image from 'next/image'
import { useState, useEffect } from 'react'
import { Quote, Play } from '@/components/ui/icons'

interface TestimonialCompany {
  name: string
  logo: string
  isActive: boolean
}

const companies: TestimonialCompany[] = [
  { name: '66Loop', logo: '/images/logos/66looplogo.avif', isActive: false },
  { name: 'Vetsak', logo: '/images/logos/Vetsak.avif', isActive: false },
  { name: 'Indepenjend Agency', logo: '/images/logos/inne.avif', isActive: false },
]

export default function TestimonialSingleCard() {
  const [activeCompany, setActiveCompany] = useState<string>('66Loop')
  const [isTransitioning, setIsTransitioning] = useState(false)



  // Handle company change with fade transition
  const handleCompanyChange = (companyName: string) => {
    if (companyName === activeCompany) return
    
    setIsTransitioning(true)
    setTimeout(() => {
      setActiveCompany(companyName)
      setTimeout(() => {
        setIsTransitioning(false)
      }, 50)
    }, 200)
  }

  // Testimonial data for each company with marked text (non-overlapping)
  const testimonials: Record<string, {
    quote: string
    name: string
    role: string
    image: string
    videoUrl?: string
  }> = {
    '66Loop': {
      quote: "Auto splits are a game-changer! My 13-person team churns out hundreds of comments across 10+ projects weekly. Hypertask has become my **digital home base** - I practically **live in it all day**.",
      name: "Rana Faizan",
      role: "66Loop CEO",
      image: "/rana_faizan.avif",
      videoUrl: "#"
    },
    'Vetsak': {
      quote: "Unstructured communication between Slack, Whatsapp and Asana is **down by 80%** for my team of 7. Everyone actually **reads and replies** to their project messages now.",
      name: "Marco Schache",
      role: "Vetsak Design Lead",
      image: "/marco_schache.avif",
      videoUrl: "#"
    },
    'Indepenjend Agency': {
      quote: "Applying **'inbox zero' mindset** to project boards is a **game changer**. We communicate more and are **better organized**.",
      name: "Robin Morgan",
      role: "Indepenjend Agency",
      image: "/robin_morgan.avif",
      videoUrl: "#"
    }
  }

  // Function to render quote with marked text (non-overlapping)
  const renderMarkedQuote = (quote: string) => {
    // Split by ** markers but ensure we don't have overlapping patterns
    const parts = quote.split(/(\*\*[^*]+\*\*)/g)
    return parts.map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        const text = part.slice(2, -2)
        return (
          <span
            key={index}
            className="bg-white/10 px-1 py-0.5 rounded-sm inline-block"
            style={{ display: 'inline-block' }}
          >
            {text}
          </span>
        )
      }
      return <span key={index}>{part}</span>
    })
  }

  const currentTestimonial = testimonials[activeCompany]
  useEffect(() => {
    const interval = setInterval(() => {
      const currentIndex = companies.findIndex(c => c.name === activeCompany)
      const nextIndex = (currentIndex + 1) % companies.length
      handleCompanyChange(companies[nextIndex].name)
    }, 5000) // Change every 5 seconds
    return () => clearInterval(interval)
  }, [activeCompany, handleCompanyChange])
  return (
    <section className="w-full py-12 md:py-24 px-4 md:px-6 lg:px-12 VWO_528">
      <div className="max-w-[1400px] mx-auto">
        {/* Quote Icon */}
        <div className="mb-6 md:mb-8 lg:mb-12">
          <Quote 
            size={32} 
            className="text-gray-400 transform rotate-180 opacity-30 md:w-12 md:h-12"
          />
        </div>

        {/* Subheading */}
        <p className="text-gray-200 text-lg mb-4 md:mb-6 tracking-wide">
          What customers are saying…
        </p>

        {/* Main Content */}
        <div className="flex flex-col items-start justify-between gap-8 md:gap-12 lg:gap-20">
          {/* Testimonial Quote */}
          <div className="flex-1 max-w-full w-full">
            <h2 className={`text-white/80 text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-semibold leading-tight mb-8 md:mb-12 transition-opacity duration-300 ${
              isTransitioning ? 'opacity-0' : 'opacity-100'
            }`}>
              {renderMarkedQuote(currentTestimonial.quote)}
            </h2>

            {/* Author Info and Company Selection */}
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 w-full">
              {/* Author Info and Watch Video */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 w-full md:w-auto">
                <div className={`flex items-center gap-3 md:gap-4 transition-opacity duration-300 ${
                  isTransitioning ? 'opacity-0' : 'opacity-100'
                }`}>
                  <Image
                    src={currentTestimonial.image}
                    alt={currentTestimonial.name}
                    width={48}
                    height={48}
                    className="rounded-full w-12 h-12 border border-white/15 md:w-14 md:h-14 object-cover"
                  />
                  <div>
                    <p className="text-white font-semibold text-lg">
                      {currentTestimonial.name}
                    </p>
                    <p className="text-gray-400 text-sm md:text-base">
                      {currentTestimonial.role}
                    </p>
                  </div>
                </div>

                
              </div>

              {/* Company Selection - Mobile responsive */}
              <div className="flex items-center flex-wrap gap-3 md:gap-4 lg:gap-6 w-full md:w-auto  pb-2 md:pb-0">
                {companies.map((company) => (
                  <button
                    key={company.name}
                    onClick={() => handleCompanyChange(company.name)}
                    className={`
                      px-8 py-3 md:px-10 md:py-4 rounded-lg border border-white/15 bg-gradient-to-br from-white/5 via-white/2.5 to-black/30 transition-all duration-300 flex-shrink-0
                      ${activeCompany === company.name 
                        ? 'border-white/30 bg-white/10 shadow-[0_2px_16px_0_rgba(255,255,255,0.08)] scale-105'
                        : 'border-white/10 bg-white/2.5 hover:border-white/20 hover:bg-white/5'
                      }
                    `}
                  >
                    <div className="relative h-6 md:h-8 flex items-center justify-center">
                      {company.name === '66Loop' && (
                        <span className="text-white text-lg md:text-xl font-bold">66Loop</span>
                      )}
                      {company.name === 'Vetsak' && (
                        <span className="text-white text-lg md:text-xl font-bold">Vetsak</span>
                      )}
                      {company.name === 'Indepenjend Agency' && (
                        <span className="text-white text-sm md:text-lg whitespace-nowrap font-bold">Indepenjend Agency</span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

